import { useState } from 'react';
import { usePreviewDocumentMutation } from '@/entities/document';
import type { PreviewAction, PreviewDiff, SaveDocumentPayload } from '@/entities/document';
import { getErrorCode } from '@/shared/api/errorToMessage';
import type { ApiQueryError } from '@/shared/api/errorToMessage';

interface PendingDialog {
  diff: PreviewDiff;
  resolve: (confirmed: boolean) => void;
}

/** §8.5: диалог предпросмотра последствий правки проведённого документа.
 * `runWithPreview` — считает последствия, спрашивает подтверждение только если что-то реально
 * меняется («это предупреждение, а не блокировка», §8.5), и повторяет предпросмотр, если
 * `state_version` успела устареть между предпросмотром и сохранением (§8.5 [ИЗМ]). Возвращает
 * `true`, если действие выполнено, `false` — если пользователь отменил в диалоге. */
export function useSaveWithPreview() {
  const [previewDocument] = usePreviewDocumentMutation();
  const [pending, setPending] = useState<PendingDialog | null>(null);
  // §8.5: true from the moment a preview is requested until execute()/the dialog settles — lets
  // callers disable their save/unpost/cancel/delete buttons for the whole round trip, not just the
  // final mutation. Without this a fast double-click fires two concurrent preview calls (the first
  // click's preview promise hasn't resolved yet, so the dialog isn't on screen to absorb the second
  // click) — reproduced live, not hypothetical.
  const [isRunning, setIsRunning] = useState(false);

  function confirmDialog(diff: PreviewDiff): Promise<boolean> {
    return new Promise((resolve) => setPending({ diff, resolve }));
  }

  function resolvePending(confirmed: boolean) {
    pending?.resolve(confirmed);
    setPending(null);
  }

  async function runWithPreview(
    documentId: string,
    action: PreviewAction,
    rowVersion: number,
    execute: (stateVersion: string) => Promise<unknown>,
    document?: SaveDocumentPayload,
  ): Promise<boolean> {
    if (isRunning) return false;
    setIsRunning(true);
    try {
      let diff = await previewDocument({ id: documentId, body: { action, rowVersion, document } }).unwrap();
      for (;;) {
        if (diff.hasChanges) {
          const confirmed = await confirmDialog(diff);
          if (!confirmed) return false;
        }
        try {
          await execute(diff.stateVersion);
          return true;
        } catch (e) {
          if (getErrorCode(e as ApiQueryError) === 'STATE_VERSION_MISMATCH') {
            diff = await previewDocument({ id: documentId, body: { action, rowVersion, document } }).unwrap();
            continue;
          }
          throw e;
        }
      }
    } finally {
      setIsRunning(false);
    }
  }

  return { runWithPreview, pendingDiff: pending?.diff, resolvePending, isRunningPreview: isRunning };
}
